import { useEffect, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PARAM_VALUES } from "../../lib/kb";
import useListinoOptions from "../../hooks/useListinoOptions";

/**
 * Inline-editable grid cell with status colour + left-border accent.
 *  - extracted (green)   -> value from the customer text
 *  - default   (amber)   -> sector default, editable
 *  - missing   (red)     -> obligatory but not specified
 *  - confirmed (white)   -> operator-confirmed value
 *
 * v0.4.3 — when ``famiglia`` + ``voceFilters`` are passed, the dropdown is
 * populated from the MongoDB listino via /api/listino/options (only real
 * stock) with 5min cache. Falls back to static PARAM_VALUES on any error.
 */
export default function EditableCell({
  pcode,
  value,
  status,
  onChange,
  testId,
  align = "left",
  formatter,
  monoFont = false,
  // v0.4.3 FIX D: dynamic listino options
  famiglia,
  voceFilters,
  // R8 v0.5.0: motivazione AZZURRA mostrata come tooltip su hover
  // quando status === "default".
  tooltipMotivazione,
  // R11.b — Learning rule meta. When provided, the cell renders with a
  // light-blue background + small "L" badge and a tooltip showing the
  // creator + date.
  learnedRule,           // { rule_id, created_by, created_at, motivazione }
  onLearnedRuleClick,    // (rule_id) => void  — opens override modal
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [flash, setFlash] = useState(false);
  const prevStatus = useRef(status);
  const inputRef = useRef(null);

  useEffect(() => setDraft(value ?? ""), [value]);
  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  useEffect(() => {
    if (status === "extracted" && prevStatus.current !== "extracted") {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 650);
      return () => clearTimeout(t);
    }
    prevStatus.current = status;
  }, [status]);

  const staticOptions = PARAM_VALUES[pcode];
  const isNumeric = staticOptions === null;
  const listino = useListinoOptions(famiglia, pcode, voceFilters || {});
  // Prefer listino options if available AND non-empty. Fallback to static.
  const optionsFromListino =
    listino.options && listino.options.length > 0 ? listino.options : null;
  const options = optionsFromListino || staticOptions;
  const restrictedToListino = !!optionsFromListino;

  const commit = (v) => {
    onChange?.(v);
    setEditing(false);
  };

  // R11.b — Learned-rule cell visuals: light-blue bg + tooltip
  const isLearned = !!learnedRule;
  const statusClass = isLearned
    ? `tq-cell-status-learned ${flash ? "tq-cell-flash" : ""}`
    : `tq-cell-status-${status || "confirmed"} ${flash ? "tq-cell-flash" : ""}`;
  const display = formatter ? formatter(value) : value || "—";
  const alignClass = align === "right" ? "text-right" : "text-left";
  const monoClass = monoFont ? "tq-cell-mono" : "";
  // R8: sovrascrivi title attr con motivazione se la cella è AZZURRA.
  const cellTitle = (status === "default" && tooltipMotivazione)
    ? tooltipMotivazione
    : (value || "");
  const learnedStyle = isLearned
    ? { background: "#E0F2FE", border: "1px solid #BAE6FD" }
    : {};
  const learnedTitle = isLearned
    ? `Appreso da ${learnedRule.created_by || "—"} il ${
        learnedRule.created_at ? new Date(learnedRule.created_at).toLocaleDateString("it-IT") : "—"
      } — clicca per override${
        learnedRule.motivazione ? `\n\n"${learnedRule.motivazione}"` : ""
      }`
    : null;
  const finalTitle = learnedTitle || cellTitle;

  if (Array.isArray(options) && options.length > 0) {
    // R8-bis: se il value corrente NON è fra le options (es. valore
    // composito "DN40/DN25" sulle riduzioni custom), renderizza come
    // testo plain — Select shadcn altrimenti mostra cella vuota.
    const valueInOptions = options.some(
      (opt) => String(opt) === String(value || "")
    );
    if (value && !valueInOptions) {
      return (
        <div
          className={`tq-cell ${statusClass} ${alignClass} ${monoClass} text-[12px]`}
          style={{
            color: "var(--text-primary)",
          }}
          onClick={() => setEditing(true)}
          role="button"
          tabIndex={0}
          data-testid={testId}
          title={cellTitle || String(value)}
        >
          {value}
        </div>
      );
    }
    return (
      <div
        className={`tq-cell ${statusClass} relative`}
        data-testid={testId}
        title={finalTitle || cellTitle}
        style={isLearned ? {position: "relative"} : undefined}
      >
        <Select value={value || ""} onValueChange={(v) => commit(v)}>
          <SelectTrigger
            className={`h-auto py-0 px-0 border-0 bg-transparent shadow-none focus:ring-0 ${monoClass} ${alignClass} text-[12px] [&>svg]:h-3 [&>svg]:w-3 ${
              !value ? "font-normal" : ""
            }`}
            style={{
              color: value
                ? "var(--text-primary)"
                : status === "missing"
                ? "var(--semantic-red)"
                : "var(--text-muted)",
            }}
            data-testid={`${testId}-trigger`}
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {restrictedToListino && (
              <div
                className="px-2 py-1 text-[10px] font-medium sticky top-0 z-10"
                style={{
                  color: "#047857",
                  background: "#ECFDF5",
                  borderBottom: "1px solid #A7F3D0",
                }}
                data-testid={`${testId}-listino-badge`}
              >
                Solo da listino ({options.length})
              </div>
            )}
            {options.map((opt) => (
              <SelectItem key={String(opt)} value={String(opt)}>
                {String(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isLearned && (
          <span
            data-role="learned-badge"
            data-testid={`${testId}-learned-badge`}
            className="absolute top-0 right-0 text-[8px] font-bold leading-none px-[3px] py-[1px] rounded-bl cursor-pointer"
            style={{ background: "#0EA5E9", color: "white" }}
            onClick={(e) => {
              e.stopPropagation();
              onLearnedRuleClick?.(learnedRule.rule_id);
            }}
            title={learnedTitle || ""}
          >
            L
          </span>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div className={`tq-cell ${statusClass}`} data-testid={testId}>
        <input
          ref={inputRef}
          type={isNumeric ? "number" : "text"}
          className={`w-full bg-transparent outline-none border-0 p-0 text-[12px] ${monoClass} ${alignClass}`}
          style={{ color: "var(--text-primary)" }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() =>
            commit(isNumeric ? (draft === "" ? null : Number(draft)) : draft)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              setDraft(value ?? "");
              setEditing(false);
            }
          }}
          data-testid={`${testId}-input`}
        />
      </div>
    );
  }

  return (
    <div
      className={`tq-cell ${statusClass} ${alignClass} ${monoClass} text-[12px]`}
      style={{
        color: value
          ? "var(--text-primary)"
          : status === "missing"
          ? "var(--semantic-red)"
          : "var(--text-muted)",
        fontWeight: !value && status === "missing" ? 500 : 400,
        ...learnedStyle,
        position: "relative",
      }}
      onClick={(e) => {
        if (isLearned && e.target.dataset?.role === "learned-badge") {
          e.stopPropagation();
          onLearnedRuleClick?.(learnedRule.rule_id);
          return;
        }
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      role="button"
      tabIndex={0}
      data-testid={testId}
      title={finalTitle || (display === "—" ? "" : String(display))}
    >
      {display}
      {isLearned && (
        <span
          data-role="learned-badge"
          data-testid={`${testId}-learned-badge`}
          className="absolute top-0 right-0 text-[8px] font-bold leading-none px-[3px] py-[1px] rounded-bl cursor-pointer"
          style={{
            background: "#0EA5E9",
            color: "white",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onLearnedRuleClick?.(learnedRule.rule_id);
          }}
        >
          L
        </span>
      )}
    </div>
  );
}
